import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2, RefreshCw, Loader2, ArrowLeft, ChevronRight,
  AlertTriangle, Search, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 365 days', value: 365 },
];

const SORTS = [
  { label: 'Volume (window)', value: 'referrals_window' },
  { label: 'Placement rate', value: 'placement_rate' },
  { label: 'Time-to-placement (p50)', value: 'ttp_p50' },
  { label: 'All-time referrals', value: 'referrals_total' },
  { label: 'Open pipeline', value: 'open_count' },
  { label: 'Escalated', value: 'escalated_count' },
];

const TYPE_TONE = {
  hospital: 'bg-blue-100 text-blue-800 border-blue-200',
  agency: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  unknown: 'bg-slate-100 text-slate-600 border-slate-200',
};

function fmtMs(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  const d = h / 24;
  return `${d.toFixed(1)}d`;
}

function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(0)}%`;
}

export default function AdminTenantHealthDashboard() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [windowDays, setWindowDays] = useState(30);
  const [sortBy, setSortBy] = useState('referrals_window');
  const [search, setSearch] = useState('');
  const [drillId, setDrillId] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/admin/tenant-health?window_days=${windowDays}&sort_by=${sortBy}&limit=200`,
        auth,
      );
      setData(r.data);
    } catch (e) { /* silent */ } finally { setBusy(false); }
  }, [token, auth, windowDays, sortBy]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    load();
  }, [user, load]);

  const loadDrill = useCallback(async (orgId) => {
    setDrillId(orgId);
    setDrill(null);
    setDrillBusy(true);
    try {
      const r = await axios.get(
        `${API}/admin/tenant-health/${encodeURIComponent(orgId)}?window_days=${windowDays}`,
        auth,
      );
      setDrill(r.data);
    } catch (e) { /* silent */ } finally { setDrillBusy(false); }
  }, [auth, windowDays]);

  const filtered = useMemo(() => {
    if (!data?.tenants) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.tenants;
    return data.tenants.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.primary_state || '').toLowerCase().includes(q) ||
      (t.type || '').toLowerCase().includes(q),
    );
  }, [data, search]);

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center text-cn-text-muted">
        Admin access required.
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-cn-bg dark:bg-cn-bg-dark text-cn-text"
      data-testid="admin-tenant-health-page"
    >
      {/* Top bar */}
      <div className="border-b border-cn-line bg-white/70 dark:bg-slate-900/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-violet-600" />
            <h1 className="text-lg font-semibold tracking-tight">
              Tenant Health
            </h1>
            {data && (
              <span
                className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                data-testid="tenant-count-pill"
              >
                {data.tenant_count} tenants · {data.active_tenants} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="cn-btn cn-btn-ghost text-xs"
              data-testid="tenant-health-refresh"
            >
              {busy
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
            <Link
              to="/admin/ops"
              className="text-sm text-cn-text-muted hover:text-cn-text flex items-center gap-1.5"
              data-testid="back-to-ops"
            >
              <ArrowLeft className="w-4 h-4" /> Ops
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Controls */}
        <div className="cn-card p-4 flex flex-wrap items-center gap-3">
          <label className="text-xs uppercase tracking-wider text-cn-text-muted mr-1">
            Window
          </label>
          <select
            data-testid="tenant-window-select"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="text-sm border border-cn-line rounded px-2 py-1 bg-white dark:bg-slate-900"
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
          <label className="text-xs uppercase tracking-wider text-cn-text-muted ml-4 mr-1">
            Sort
          </label>
          <select
            data-testid="tenant-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-cn-line rounded px-2 py-1 bg-white dark:bg-slate-900"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-cn-text-muted" />
            <input
              data-testid="tenant-search-input"
              type="text"
              placeholder="Search name / state / type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="text-sm border border-cn-line rounded pl-7 pr-2 py-1 bg-white dark:bg-slate-900 w-64"
            />
          </div>
        </div>

        {/* Rollup KPIs */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi
              label="Referrals in window"
              value={data.totals.referrals_window}
              testId="tenant-kpi-window-refs"
            />
            <Kpi
              label="Placed in window"
              value={data.totals.placed_window}
              testId="tenant-kpi-window-placed"
            />
            <Kpi
              label="Window placement rate"
              value={fmtPct(data.totals.placement_rate_window)}
              tone={data.totals.placement_rate_window >= 0.7 ? 'emerald' :
                    data.totals.placement_rate_window >= 0.4 ? 'amber' : 'red'}
              testId="tenant-kpi-window-rate"
            />
            <Kpi
              label="Active tenants"
              value={`${data.active_tenants} / ${data.tenant_count}`}
              testId="tenant-kpi-active"
            />
          </div>
        )}

        {/* Table */}
        <div className="cn-card overflow-hidden" data-testid="tenant-table-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr className="text-left text-[0.65rem] uppercase tracking-wider text-cn-text-muted border-b border-cn-line">
                  <th className="px-4 py-3">Tenant</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3 text-right">Refs (window)</th>
                  <th className="px-3 py-3 text-right">Placed (window)</th>
                  <th className="px-3 py-3 text-right">Rate (all-time)</th>
                  <th className="px-3 py-3 text-right">TTP p50</th>
                  <th className="px-3 py-3 text-right">TTP p90</th>
                  <th className="px-3 py-3 text-right">Open</th>
                  <th className="px-3 py-3 text-right">Escal.</th>
                  <th className="px-3 py-3 text-right">Errors 24h</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody data-testid="tenant-table-body">
                {!data && (
                  <tr><td colSpan={11} className="text-center py-10 text-cn-text-muted">
                    Loading…
                  </td></tr>
                )}
                {data && filtered.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-10 text-cn-text-muted italic">
                    No tenants match “{search}”.
                  </td></tr>
                )}
                {filtered.map((t) => (
                  <tr
                    key={t.org_id || '_unassigned'}
                    className="border-b border-cn-line last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                    data-testid={`tenant-row-${t.org_id || 'unassigned'}`}
                    onClick={() => loadDrill(t.org_id || '_unassigned')}
                  >
                    <td className="px-4 py-3 font-medium truncate max-w-[18rem]">
                      {t.name}
                      {t.primary_state && (
                        <span className="ml-2 text-xs text-cn-text-muted">
                          {t.primary_state}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-[0.6rem] font-bold uppercase px-1.5 py-0.5 rounded border ${TYPE_TONE[t.type] || TYPE_TONE.unknown}`}>
                        {t.type}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {t.referrals_window}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {t.placed_window}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <RateBadge value={t.placement_rate} />
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {fmtMs(t.ttp_p50_ms)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      {fmtMs(t.ttp_p90_ms)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {t.open_count}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {t.escalated_count > 0 ? (
                        <span className="text-amber-700 font-semibold">{t.escalated_count}</span>
                      ) : t.escalated_count}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {(t.top_errors_24h?.length || 0) > 0 ? (
                        <span className="text-red-700 font-semibold inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {t.top_errors_24h.reduce((a, e) => a + (e.count || 0), 0)}
                        </span>
                      ) : (
                        <span className="text-cn-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-cn-text-muted inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-cn-text-muted text-center">
          {data && (<>Snapshot at {data.at.slice(11, 19)} UTC · window = {data.window_days}d · click a row to drill down</>)}
        </p>
      </div>

      {/* Drill-down modal */}
      {drillId && (
        <DrillModal
          orgId={drillId}
          data={drill}
          busy={drillBusy}
          onClose={() => { setDrillId(null); setDrill(null); }}
        />
      )}
    </div>
  );
}

function RateBadge({ value }) {
  const pct = value * 100;
  let tone = 'text-slate-600 bg-slate-100';
  if (pct >= 70) tone = 'text-emerald-800 bg-emerald-100';
  else if (pct >= 40) tone = 'text-amber-800 bg-amber-100';
  else if (value > 0) tone = 'text-red-800 bg-red-100';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${tone}`}>
      {value == null ? '—' : `${pct.toFixed(0)}%`}
    </span>
  );
}

function Kpi({ label, value, tone, testId }) {
  const toneMap = {
    red: 'text-red-700', amber: 'text-amber-700',
    emerald: 'text-emerald-700', slate: 'text-slate-700',
  };
  return (
    <div className="cn-card p-4" data-testid={testId}>
      <div className="text-[0.65rem] uppercase tracking-wider text-cn-text-muted mb-1">
        {label}
      </div>
      <div className={`text-2xl font-semibold ${toneMap[tone] || toneMap.slate}`}>
        {value}
      </div>
    </div>
  );
}

function DrillModal({ orgId, data, busy, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="tenant-drill-modal"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-cn-line flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-cn-text-muted mb-1">
              Tenant deep-dive
            </div>
            <h2 className="text-xl font-semibold" data-testid="drill-tenant-name">
              {data?.org?.name || '…'}
            </h2>
            {data?.org?.type && (
              <div className="text-xs text-cn-text-muted mt-1">
                {data.org.type}
                {data.org.primary_state && ` · ${data.org.primary_state}`}
                {data.org.contact_email && ` · ${data.org.contact_email}`}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="cn-btn cn-btn-ghost text-xs"
            data-testid="drill-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {busy && !data && (
          <div className="p-10 text-center text-cn-text-muted">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            {/* TTP stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi
                label="TTP p50"
                value={fmtMs(data.time_to_placement.p50_ms)}
                testId="drill-kpi-ttp-p50"
              />
              <Kpi
                label="TTP p90"
                value={fmtMs(data.time_to_placement.p90_ms)}
                testId="drill-kpi-ttp-p90"
              />
              <Kpi
                label="TTP samples"
                value={data.time_to_placement.sample_count}
                testId="drill-kpi-ttp-samples"
              />
              <Kpi
                label="Errors 24h"
                value={data.recent_errors_24h.length}
                tone={data.recent_errors_24h.length > 0 ? 'red' : 'emerald'}
                testId="drill-kpi-errors"
              />
            </div>

            {/* Status distribution */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted mb-2">
                Referral status ({data.window_days}d window)
              </h3>
              <div className="flex flex-wrap gap-2" data-testid="drill-status-dist">
                {Object.entries(data.status_distribution_window || {}).map(([k, v]) => (
                  <div key={k} className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">
                    <span className="uppercase text-cn-text-muted">{k}</span>
                    <span className="ml-2 font-semibold">{v}</span>
                  </div>
                ))}
                {Object.keys(data.status_distribution_window || {}).length === 0 && (
                  <span className="text-xs text-cn-text-muted italic">
                    No referrals in window.
                  </span>
                )}
              </div>
            </div>

            {/* Orchestrator terminal distribution */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted mb-2">
                Orchestrator terminal states (all-time)
              </h3>
              <div className="flex flex-wrap gap-2" data-testid="drill-terminal-dist">
                {Object.entries(data.orchestrator_terminal_distribution || {}).map(([k, v]) => (
                  <div key={k} className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">
                    <span className="uppercase text-cn-text-muted">{k}</span>
                    <span className="ml-2 font-semibold">{v}</span>
                  </div>
                ))}
                {Object.keys(data.orchestrator_terminal_distribution || {}).length === 0 && (
                  <span className="text-xs text-cn-text-muted italic">
                    No orchestrator traces.
                  </span>
                )}
              </div>
            </div>

            {/* Coverage distribution */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted mb-2">
                Coverage-gate outcomes (all-time)
              </h3>
              <div className="flex flex-wrap gap-2" data-testid="drill-coverage-dist">
                {Object.entries(data.coverage_distribution || {}).map(([k, v]) => (
                  <div key={k} className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">
                    <span className="uppercase text-cn-text-muted">{k}</span>
                    <span className="ml-2 font-semibold">{v}</span>
                  </div>
                ))}
                {Object.keys(data.coverage_distribution || {}).length === 0 && (
                  <span className="text-xs text-cn-text-muted italic">
                    No coverage evaluations.
                  </span>
                )}
              </div>
            </div>

            {/* Recent errors */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted mb-2">
                Platform errors last 24h
              </h3>
              {data.recent_errors_24h.length === 0 ? (
                <div className="text-xs text-cn-text-muted italic p-2">
                  No 5xx errors for this tenant.
                </div>
              ) : (
                <div className="space-y-1" data-testid="drill-errors-list">
                  {data.recent_errors_24h.map((e, i) => (
                    <div key={i} className="text-xs border-b border-cn-line last:border-0 py-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-red-700 font-semibold">{e.status}</span>
                        <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{e.method}</code>
                        <span className="truncate">{e.path}</span>
                        <span className="text-cn-text-muted ml-auto">{e.at?.slice(11, 19)}</span>
                      </div>
                      <div className="text-cn-text-muted mt-0.5 ml-1">
                        <code>{e.exception_type}</code>: {e.exception_msg?.slice(0, 120)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent referrals */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted mb-2">
                Recent referrals (last 20)
              </h3>
              {data.recent_referrals.length === 0 ? (
                <div className="text-xs text-cn-text-muted italic p-2">
                  No referrals for this tenant yet.
                </div>
              ) : (
                <div className="space-y-1" data-testid="drill-referrals-list">
                  {data.recent_referrals.map((r) => (
                    <div key={r.id} className="text-xs border-b border-cn-line last:border-0 py-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill status={r.status} />
                        <span className="text-cn-text-muted">
                          {r.care_type} · {r.urgency}
                        </span>
                        <span className="ml-auto text-cn-text-muted">
                          {r.created_at?.slice(0, 10)}
                        </span>
                      </div>
                      {r.primary_diagnosis && (
                        <div className="text-cn-text-muted mt-0.5 ml-1 truncate">
                          {r.primary_diagnosis}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const tone = {
    placed: 'bg-emerald-100 text-emerald-800',
    matched: 'bg-emerald-100 text-emerald-800',
    closed: 'bg-slate-200 text-slate-700',
    first_visit_committed: 'bg-blue-100 text-blue-800',
    escalated: 'bg-red-100 text-red-800',
    matching: 'bg-violet-100 text-violet-800',
    contacted: 'bg-amber-100 text-amber-800',
    pending: 'bg-amber-100 text-amber-800',
    new: 'bg-slate-100 text-slate-700',
  }[status] || 'bg-slate-100 text-slate-600';
  return (
    <span className={`text-[0.6rem] font-bold uppercase px-1.5 py-0.5 rounded ${tone}`}>
      {status}
    </span>
  );
}
